import { User, onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { auth, db } from '@/core/firebase'
import { showView } from '@/core/views'
import { setCurrentUser, setCurrentUserRole, adminCredentials, setAdminCredentials, currentUserRole, currentView } from '@/core/state'
import { populateCompiledDataTable } from '../data-hub/table'
import { loadGeminiConfig } from '@/config/gemini'
import { authError, signupError, hideLoading } from '@/core/ui'
import { viewCompiledAnalysis } from '@/analysis/actions'
import { clearSummariesCache } from '@/services/localCacheService'

import { collection, query, where, getDocs, limit, orderBy, getDocFromServer } from 'firebase/firestore' // <-- 1. IMPORT ADDED
import { paths } from '../db/paths' // Import the path builder
import { AccountSubscription, FREE_PLAN, UserSchema } from '../db/schema' // Import FREE_PLAN constant
import type { Branch, BranchMembership, Account } from '../db/schema' // Types for Firestore docs
import { setAccountContext, resetAnalysisState, updateAnalysisConfig } from '../store/api' // Import the new Zustand action
// Add these imports at the top of apps/web/src/auth/auth.ts
import { resetAnalysisState } from '@/store/api'
import { clearSummariesCache } from '@/services/localCacheService'
import { collection, query, where, getDocs, limit, orderBy, getDocFromServer, getDocsFromServer } from 'firebase/firestore' // <-- 1. IMPORT ADDED: getDocsFromServer

// --- START NEW HELPER FUNCTION ---
/**
 * Fetches Account, Branch, and Subscription details for a given user ID from Firestore.
 * @param uid The user's unique identifier.
 * @returns An object containing account context.
 */
const PLAN_RANK: Record<'free' | 'standard' | 'enterprise', number> = {
  free: 0,
  standard: 1,
  enterprise: 2,
}

async function fetchAccountPlanForUser(accountId: string, uid: string): Promise<'free' | 'standard' | 'enterprise'> {
  try {
    const accountDoc = await getDoc(doc(db, paths.accounts(accountId).path))
    if (!accountDoc.exists()) return 'free'
    const accountData = accountDoc.data() as Account
    if (accountData.ownerUid !== uid) return 'free'

    const subsRef = collection(db, paths.accounts(accountId).subscriptions.collectionPath)
    const subsSnapshot = await getDocs(query(subsRef, orderBy('activatedAt', 'desc'), limit(1)))
    if (subsSnapshot.empty) return 'free'

    const subData = subsSnapshot.docs[0].data() as AccountSubscription
    const plan = subData.planType
    if (plan === 'enterprise' || plan === 'standard') {
      return plan
    }
    if ((plan as string) === 'premium') return 'enterprise'
    return 'free'
  } catch (error) {
    console.error('[AUTH] Failed to fetch account plan:', error)
    return 'free'
  }
}

async function fetchAccountContext(uid: string): Promise<{
  accountId: string | null
  planType: 'free' | 'standard' | 'enterprise'
  role: 'owner' | 'member' | null
  userBrands: { brandId: string, brandName: string }[]
  selectedBrandId: string | null
  userBranches: { branchId: string, branchName: string, role: 'owner' | 'member', isDefaultName: boolean, accountId: string, brandId: string | null }[]
  selectedBranchId: string | null
}> {
  let accountId: string | null = null
  let planType: 'free' | 'standard' | 'enterprise' = FREE_PLAN
  let role: 'owner' | 'member' | null = null
  let userBranches: { branchId: string, branchName: string, role: 'owner' | 'member', isDefaultName: boolean, accountId: string, brandId: string | null }[] = []
  let selectedBranchId: string | null = null
  let userBrands: { brandId: string, brandName: string }[] = []
  let selectedBrandId: string | null = null

  try {
    // Query memberships first (works for both owners and members)
    const membershipsRef = collection(db, paths.memberships.collectionPath)
    const membershipQuery = query(membershipsRef, where('userId', '==', uid))
    
    console.log(`[AUTH] Fetching memberships FROM SERVER for user ${uid}...`)
    const membershipSnapshot = await getDocsFromServer(membershipQuery)

    if (!membershipSnapshot.empty) {
      console.log(`[AUTH] Found ${membershipSnapshot.docs.length} membership(s).`)
      const branchesAcc: { branchId: string, branchName: string, role: 'owner' | 'member', accountId: string, isDefaultName: boolean, brandId: string | null }[] = []
      for (const membershipDoc of membershipSnapshot.docs) {
        const membershipData = membershipDoc.data() as BranchMembership
        const branchId = membershipData.branchId
        const memberRole = (membershipData.role || 'member') as 'owner' | 'member'
        if (branchId) {
          const branchRef = doc(db, paths.branches(branchId).path)
          
          console.log(`[AUTH] Fetching branch ${branchId} FROM SERVER...`)
          const branchSnap = await getDocFromServer(branchRef)
          
          if (branchSnap.exists()) {
            const bdata = branchSnap.data() as Branch
            
            console.log(`[AUTH] RAW DATA for ${branchId}:`, JSON.stringify(bdata))
            
            branchesAcc.push({
              branchId,
              branchName: bdata.branchName || `Branch ${branchId}`,
              role: memberRole,
              accountId: bdata.accountId,
              isDefaultName: bdata.isDefaultName === false ? false : true,
              brandId: bdata.brandId || null,
            })
          } else {
            console.warn(`[AUTH] Membership found for branch ${branchId}, but branch document does not exist!`)
          }
        } else {
           console.warn(`[AUTH] Membership document ${membershipDoc.id} has no branchId.`)
        }
      }

      userBranches = branchesAcc.map(({ branchId, branchName, role, isDefaultName, accountId: branchAccountId, brandId }) => ({
        branchId,
        branchName,
        role,
        isDefaultName: isDefaultName === false ? false : true,
        accountId: branchAccountId,
        brandId: brandId,
      }))
      userBranches.sort((a, b) => a.branchName.localeCompare(b.branchName))

      let stored: string | null = null
      try {
        stored = localStorage.getItem('selectedBranchId')
      } catch (_e) {
        // ignore storage access errors
      }
      const found = stored && userBranches.find((b) => b.branchId === stored) ? stored : null
      selectedBranchId = found || (userBranches[0] ? userBranches[0].branchId : null)

      // Derive accountId and role from selected branch/membership
      accountId = branchesAcc.find((b) => b.branchId === selectedBranchId)?.accountId || branchesAcc[0]?.accountId || null
      role = userBranches.find((b) => b.branchId === selectedBranchId)?.role || (userBranches[0]?.role || null)
    } else {
      console.log(`[AUTH] No membership documents found for user ${uid}.`)
    }

    // If still no accountId, try owner account lookup (legacy/owner-only path)
    if (!accountId) {
      console.log(`[AUTH] No accountId from memberships, checking 'ownerUid'...`)
      const accountsRef = collection(db, paths.accounts.collectionPath)
      const accountQuery = query(accountsRef, where('ownerUid', '==', uid), limit(1))
      const accountSnapshot = await getDocs(accountQuery) // Cache is fine here
      if (!accountSnapshot.empty) {
        const accountDoc = accountSnapshot.docs[0]!
        accountId = accountDoc.id
        const accountData = accountDoc.data() as Account
        role = accountData.ownerUid === uid ? 'owner' : (role || 'member')
        console.log(`[AUTH] Found account ${accountId} via ownerUid.`)
      }
    }

    // Determine plan type from active subscription
    const ownedAccountIds = Array.from(new Set(userBranches.filter((branch) => branch.role === 'owner').map((branch) => branch.accountId))).filter(Boolean)
    if (ownedAccountIds.length > 0) {
      let highestPlan: 'free' | 'standard' | 'enterprise' = 'free'
      for (const ownedAccountId of ownedAccountIds) {
        const plan = await fetchAccountPlanForUser(ownedAccountId, uid)
        if (PLAN_RANK[plan] > PLAN_RANK[highestPlan]) {
          highestPlan = plan
        }
      }
      planType = highestPlan
    } else {
      planType = FREE_PLAN
    }

    if (accountId && planType === FREE_PLAN && role === 'owner') {
      const subscriptionsPath = paths.accounts(accountId).subscriptions
      const subscriptionsRef = collection(db, subscriptionsPath.collectionPath)
      const subsSnapshot = await getDocs(subscriptionsRef)
      let activeSubscription: { planType: 'free' | 'standard' | 'enterprise'; expiryDate: Date | null } | null = null
      const now = new Date()
      subsSnapshot.docs.forEach((subDoc) => {
        const subData = subDoc.data() as AccountSubscription
        const branchIds = (subData as any).branchIds as string[] | undefined
        const scopedOk = Array.isArray(branchIds)
          ? (branchIds.length > 0 ? (!selectedBranchId || branchIds.includes(selectedBranchId)) : true)
          : true

        if (!scopedOk) return

        const tryToDate = (v: any): Date | null => {
          if (!v) return null
          if (typeof v.toDate === 'function') {
            try { return v.toDate() as Date } catch { return null }
          }
          if (v instanceof Date) return v
          const parsed = new Date(v)
          return isNaN(parsed.getTime()) ? null : parsed
        }

        let expiryDate: Date | null = null
        expiryDate = tryToDate((subData as any).possibleExpiredAt)
          || tryToDate(subData.fromPaymentGateway?.expiredAt)
          || ((): Date | null => {
                const act = tryToDate((subData as any).activatedAt)
                if (!act) return null
                const d = new Date(act)
                d.setDate(d.getDate() + 30)
                return d
              })()

        const isActive = (() => {
          if (expiryDate) return expiryDate > now
          return subData.planType !== 'free'
        })()

        if (!isActive) return

        if (
          !activeSubscription
          || (expiryDate && (!activeSubscription.expiryDate || expiryDate > activeSubscription.expiryDate))
          || (!activeSubscription.expiryDate && !expiryDate && subData.planType !== 'free')
        ) {
          activeSubscription = { planType: subData.planType, expiryDate }
        }
      })

      if (activeSubscription) {
        const subPlanType = (activeSubscription.planType || FREE_PLAN) as 'free' | 'standard' | 'enterprise'
        planType = subPlanType
      } else {
        planType = FREE_PLAN
      }
    }

    // --- [THIS IS THE FIX] ---
    // Moved the "Fetch Brands" logic to the END of the try block.
    if (accountId) {
      console.log(`[AUTH] Fetching brands for account ${accountId}...`)
      const brandsRef = collection(db, paths.brands.collectionPath)
      
      // REVERTED to the correct query: 'accountId' (string) not 'ownerAccountIds' (array)
      const brandsQuery = query(brandsRef, where('accountId', '==', accountId))
      
      const brandsSnapshot = await getDocs(brandsQuery) // Cache is fine here
      
      userBrands = brandsSnapshot.docs.map((doc) => ({
        brandId: doc.id,
        brandName: doc.data().brandName || 'Unnamed Brand',
      })).sort((a, b) => a.brandName.localeCompare(b.brandName))
      console.log(`[AUTH] Found ${userBrands.length} brand(s).`)

      // Persist brand selection
      let storedBrand: string | null = null
      try {
        storedBrand = localStorage.getItem('selectedBrandId')
      } catch (_e) { /* ignore */ }
      selectedBrandId = (storedBrand && userBrands.find(b => b.brandId === storedBrand))
        ? storedBrand
        : (userBrands[0]?.brandId || null)
    } else {
      console.log(`[AUTH] No accountId found, skipping brand fetch.`)
    }
    // --- [END FIX] ---

  } catch (error) {
    console.error('Error fetching account context from Firestore:', error)
    accountId = null
    planType = FREE_PLAN
    role = null
    userBrands = []
    selectedBrandId = null
    userBranches = []
    selectedBranchId = null
  }

  return { accountId, planType, role, userBrands, selectedBrandId, userBranches, selectedBranchId }
}
// --- END NEW HELPER FUNCTION ---

/**
 * Ensures a user document exists in Firestore with basic profile information.
 * @param uid The user's unique identifier.
 * @param email The user's email address.
 * @param role The user's role (defaults to 'user').
 */
async function ensureUserDocument(uid: string, email: string | null, role = 'user'): Promise<void> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  if (!userSnap.exists()) {
    try {
      // Create user document with all required fields aligned with UserSchema
      const now = new Date()
      await setDoc(userRef, UserSchema.parse({
        uid,
        email,
        role,
        hasUploadedData: false,
        // Timestamp metadata fields (aligned with TimestampMetadataSchema)
        createdAt: now,
        createdBy: uid, // Self-reference for user creation
        updatedAt: now,
        updatedBy: uid,
        // Optional fields with sensible defaults
        fullName: email?.split('@')[0] || 'User', // Default to email prefix
      }))
    } catch (error) {
      console.error('Error creating user document:', error)
    }
  }
}

/**
 * Fetches the user's role and data status, then configures the UI.
 * @param user The Firebase Auth user object.
 */
export async function fetchUserRoleAndSetupUI(user: User): Promise<void> {
  // Check admin status from Firestore (replaces custom claims)
  const { isUserAdmin } = await import('@/store/api')
  const isAdmin = await isUserAdmin(user.uid)

  const userRole: 'user' | 'admin' = isAdmin ? 'admin' : 'user'
  setCurrentUserRole(userRole)

  // Ensure user document exists (for legacy data)
  const userDocRef = doc(db, 'users', user.uid)
  const userDocSnap = await getDoc(userDocRef)
  if (!userDocSnap.exists()) {
    await ensureUserDocument(user.uid, user.email, 'user')
  }

  // --- START MODIFICATION ---
  // 1. Get the hasUploadedData flag from the user's document
  // Default to 'false' if the field doesn't exist on the document
  const hasUploadedData = userDocSnap.data()?.hasUploadedData ?? false
  // --- END MODIFICATION ---
  
  // Fetch account context using the helper function
  console.log('Fetching account context for user:', user.uid)
  const { accountId, planType, role, userBrands, selectedBrandId, userBranches, selectedBranchId } = await fetchAccountContext(user.uid)
  
  // Update Zustand store
  setAccountContext({ accountId, planType, role, userBrands, selectedBrandId, userBranches, selectedBranchId })
  
  // --- START MODIFICATION (JOB_10) ---
  // 2. Set the hasUploadedData flag in the global store *using the correct function*
  updateAnalysisConfig('hasUploadedData', hasUploadedData)
  // --- END MODIFICATION ---
  
  // --- NEW LOG ---
  console.log('[AUTH] FINAL userBranches array being set to context:', JSON.stringify(userBranches))
  // --- END LOG ---
  
  // 3. Add the new console.log flag (from Job_6)
  console.log(`[AUTH] User 'hasUploadedData' flag is set to: ${hasUploadedData}`)

  console.log('Admin status:', { isAdmin, userRole })

  // Show/hide admin UI elements based on isAdmin flag (app-level)
  document.getElementById('user-management-btn')?.classList.toggle('hidden', !isAdmin)
  document.getElementById('konfigurasi-btn')?.classList.toggle('hidden', !isAdmin)
  document.getElementById('premium-goto-user-management-btn')?.parentElement?.classList.toggle('hidden', !isAdmin)

  if (isAdmin) {
    loadGeminiConfig()
  }

  // --- [JOB_3] BRAND CREATION GATE (REMOVED) ---
  // We remove the gate logic from here.
  // We will ALWAYS load the premium view, and let ITS OWN controller
  // decide whether to show the dashboard or the brand gate.
  await viewCompiledAnalysis('premium-analysis')
  // --- [END JOB_3] ---
}
/**
 * Initializes all authentication-related listeners and form handlers for the application.
 */
export function initializeAuth(): void {
  // Main listener for changes in the user's sign-in state.
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      // User is logging IN
      setCurrentUser(user)
      if (!adminCredentials) {
        
        if (currentView === 'auth') {
          // This is a fresh login. Force a full page reload.
          window.location.reload();
        } else {
          // This is just a session refresh (e.g., user re-opened the tab).
          await fetchUserRoleAndSetupUI(user)
          
          // --- [THIS IS THE FIX] ---
          // Hide the loader *after* the UI is ready
          hideLoading()
          // --- [END FIX] ---
        }

      }
    } else {
      // User is logging OUT
      if (adminCredentials) {
        // Handle admin re-login after creating a new user.
        signInWithEmailAndPassword(auth, adminCredentials.email, adminCredentials.password)
          .then(async (userCredential) => {
            await fetchUserRoleAndSetupUI(userCredential.user)
            setAdminCredentials(null)
            showView('usermanagement')
            
            // --- [THIS IS THE FIX] ---
            // Hide loader after admin re-auth
            hideLoading()
            // --- [END FIX] ---
          })
          .catch((err) => {
            console.error('Admin re-login failed:', err)
            setAdminCredentials(null)
            showView('auth')
            
            // --- [THIS IS THE FIX] ---
            // Hide loader even if re-auth fails
            hideLoading()
            // --- [END FIX] ---
          })
      } else {
        // This is the normal logout logic
        try {
          resetAnalysisState()
          await clearSummariesCache()
          console.log('[Auth] Cleared all local data and cache on auth state change.')
        } catch (error) {
          console.error('[Auth] Failed to clear local data on auth state change:', error)
        }
        
        setCurrentUser(null)
        setCurrentUserRole('user')
        showView('auth')
        document.getElementById('user-management-btn')?.classList.add('hidden')
        
        // --- [THIS IS THE FIX] ---
        // Hide the loader to reveal the login page
        hideLoading()
        // --- [END FIX] ---
      }
    }
  })

  // --- Form and Button Listeners ---
  document.getElementById('show-signup-link')?.addEventListener('click', (e) => {
    e.preventDefault()
    document.getElementById('login-form')?.classList.add('hidden')
    document.getElementById('signup-form')?.classList.remove('hidden')
  })

  document.getElementById('show-login-link')?.addEventListener('click', (e) => {
    e.preventDefault()
    document.getElementById('signup-form')?.classList.add('hidden')
    document.getElementById('login-form')?.classList.remove('hidden')
  })

  document.getElementById('login-form')?.addEventListener('submit', (e) => {
    e.preventDefault()
    const email = (document.getElementById('login-email') as HTMLInputElement).value
    const password = (document.getElementById('login-password') as HTMLInputElement).value
    signInWithEmailAndPassword(auth, email, password).catch((error) => {
      if (authError) {
        authError.textContent = error.message
        authError.classList.remove('hidden')
      }
    })
  })

  document.getElementById('signup-form')?.addEventListener('submit', (e) => {
    e.preventDefault()
    const email = (document.getElementById('signup-email') as HTMLInputElement).value
    const password = (document.getElementById('signup-password') as HTMLInputElement).value
    createUserWithEmailAndPassword(auth, email, password).catch((error) => {
      if (signupError) {
        signupError.textContent = error.message
        signupError.classList.remove('hidden')
      }
    })
  })

  document.getElementById('logout-btn')?.addEventListener('click', async () => {
    try {
      // 1. Clear the in-memory state
      resetAnalysisState()
      // 2. Clear the persistent IndexedDB cache
      await clearSummariesCache()
      console.log('[Logout] Cleared all local data and cache.')
    } catch (error) {
      console.error('[Logout] Failed to clear local data:', error)
    } finally {
      // 3. Sign out the user
      signOut(auth)
    }
  })
}
